/*
 * tombini-game.js
 * Motore del minigioco match-3 "Tombini" (ROJI). Nessun framework,
 * nessuno step di build: JS vanilla pensato per vivere dentro un tema
 * Shopify Liquid.
 *
 * Come inserirlo in un tema Shopify:
 * - Copia questo file nella cartella "Assets" del tema
 *   (Admin → Negozio online → Temi → Modifica codice → Assets → Aggiungi asset).
 * - Nella sezione/template che mostra il gioco, includilo con:
 *     {{ 'tombini-game.js' | asset_url | script_tag }}
 * - Il markup deve contenere un contenitore con classe "tombini-game"
 *   (vedi tombini-game.liquid). Lo script si avvia da solo cercando
 *   tutti gli elementi ".tombini-game" presenti nella pagina.
 *
 * Per sostituire i placeholder colorati con grafiche reali, valorizza
 * gli attributi data-img-1 ... data-img-6 sul contenitore ".tombini-game"
 * (vedi tombini-game.liquid): non serve toccare questo file.
 */
(function () {
  'use strict';

  var GRID_SIZE = 8; // dimensione griglia, facilmente modificabile
  var TYPE_COUNT = 6;
  var GAP = 6; // px, deve rispecchiare la spaziatura visiva desiderata
  var FALL_MS = 260;
  var CLEAR_MS = 220;
  var COLORS = ['#c0392b', '#e0b400', '#2f9e5c', '#2d7dbf', '#8e44ad', '#e07b28'];
  var NAMES = ['Tombino 1', 'Tombino 2', 'Tombino 3', 'Tombino 4', 'Tombino 5', 'Tombino 6'];

  var SCORE_TILE = 10;
  var SCORE_STRIPED = 30;
  var SCORE_WRAPPED = 60;
  var SCORE_COLORBOMB = 100;

  function sleep(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  function randInt(n) {
    return Math.floor(Math.random() * n);
  }

  function shuffle(arr) {
    for (var i = arr.length - 1; i > 0; i--) {
      var j = randInt(i + 1);
      var tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
    }
    return arr;
  }

  var uid = 0;

  function TombiniGame(root) {
    this.root = root;
    this.boardEl = root.querySelector('.tombini-game__board');
    this.scoreEl = root.querySelector('.tombini-game__score-value');
    this.restartBtn = root.querySelector('.tombini-game__restart');
    this.messageEl = root.querySelector('.tombini-game__message');
    this.images = [];
    for (var i = 1; i <= TYPE_COUNT; i++) {
      this.images.push(root.getAttribute('data-img-' + i) || '');
    }
    this.board = [];
    this.score = 0;
    this.isBusy = false;
    this.selected = null;
    this.dragStart = null;
    this.slotSize = 0;

    this._onResize = this._layout.bind(this);
    window.addEventListener('resize', this._debounce(this._onResize, 120));

    if (this.restartBtn) {
      this.restartBtn.addEventListener('click', this.restart.bind(this));
    }

    this._buildInitialBoard();
  }

  TombiniGame.prototype._debounce = function (fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      var self = this;
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  };

  TombiniGame.prototype._setMessage = function (text) {
    if (!this.messageEl) return;
    if (text) {
      this.messageEl.textContent = text;
      this.messageEl.classList.add('is-visible');
    } else {
      this.messageEl.classList.remove('is-visible');
    }
  };

  /* ---------------------------------------------------------------
   * Costruzione board
   * ------------------------------------------------------------- */

  TombiniGame.prototype._randomTypeNoMatch = function (row, col) {
    var forbidden = [];
    var left1 = this.board[row] && this.board[row][col - 1];
    var left2 = this.board[row] && this.board[row][col - 2];
    if (left1 && left2 && !left1.special && !left2.special && left1.type === left2.type) {
      forbidden.push(left1.type);
    }
    var up1 = this.board[row - 1] && this.board[row - 1][col];
    var up2 = this.board[row - 2] && this.board[row - 2][col];
    if (up1 && up2 && !up1.special && !up2.special && up1.type === up2.type) {
      forbidden.push(up1.type);
    }
    var options = [];
    for (var t = 1; t <= TYPE_COUNT; t++) {
      if (forbidden.indexOf(t) === -1) options.push(t);
    }
    if (!options.length) options = [1, 2, 3, 4, 5, 6];
    return options[randInt(options.length)];
  };

  TombiniGame.prototype._createTile = function (row, col, type, special) {
    var tile = {
      id: ++uid,
      type: type,
      special: special || null,
      row: row,
      col: col,
      el: null,
      innerEl: null,
      specialEl: null
    };
    tile.el = document.createElement('div');
    tile.el.className = 'tombini-game__cell';
    tile.el.setAttribute('role', 'button');
    tile.el.setAttribute('tabindex', '0');

    var inner = document.createElement('div');
    inner.className = 'tombini-game__cell-inner';
    tile.innerEl = inner;

    var special_ = document.createElement('div');
    special_.className = 'tombini-game__cell-special';
    tile.specialEl = special_;

    tile.el.appendChild(inner);
    tile.el.appendChild(special_);

    var self = this;
    tile.el.addEventListener('pointerdown', function (ev) {
      self._onPointerDown(tile, ev);
    });
    tile.el.addEventListener('pointerup', function (ev) {
      self._onPointerUp(tile, ev);
    });
    tile.el.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        self._handleSelect(tile);
      }
    });

    this._paintTile(tile);
    return tile;
  };

  TombiniGame.prototype._paintTile = function (tile) {
    var img = this.images[tile.type - 1];
    tile.innerEl.innerHTML = '';
    tile.innerEl.style.backgroundColor = COLORS[tile.type - 1];
    tile.el.setAttribute('aria-label', NAMES[tile.type - 1]);
    if (img) {
      var imgEl = document.createElement('img');
      imgEl.src = img;
      imgEl.alt = NAMES[tile.type - 1];
      imgEl.draggable = false;
      tile.innerEl.appendChild(imgEl);
    } else {
      var span = document.createElement('span');
      span.textContent = String(tile.type);
      span.style.color = '#fff';
      span.style.fontWeight = '700';
      span.style.fontSize = '42%';
      span.style.textShadow = '0 1px 2px rgba(0,0,0,0.35)';
      tile.innerEl.appendChild(span);
    }

    tile.el.classList.remove(
      'tombini-game__cell--striped-h',
      'tombini-game__cell--striped-v',
      'tombini-game__cell--wrapped',
      'tombini-game__cell--colorbomb'
    );
    if (tile.special === 'stripedH') tile.el.classList.add('tombini-game__cell--striped-h');
    else if (tile.special === 'stripedV') tile.el.classList.add('tombini-game__cell--striped-v');
    else if (tile.special === 'wrapped') tile.el.classList.add('tombini-game__cell--wrapped');
    else if (tile.special === 'colorbomb') tile.el.classList.add('tombini-game__cell--colorbomb');
  };

  TombiniGame.prototype._buildInitialBoard = function () {
    this.boardEl.innerHTML = '';
    this.board = [];
    for (var row = 0; row < GRID_SIZE; row++) {
      this.board.push([]);
      for (var col = 0; col < GRID_SIZE; col++) {
        var type = this._randomTypeNoMatch(row, col);
        var tile = this._createTile(row, col, type, null);
        this.board[row].push(tile);
        this.boardEl.appendChild(tile.el);
      }
    }
    this._layout(true);
    this._ensureSolvable();
  };

  /* ---------------------------------------------------------------
   * Layout / posizionamento
   * ------------------------------------------------------------- */

  TombiniGame.prototype._layout = function (instant) {
    var width = this.boardEl.clientWidth || this.boardEl.getBoundingClientRect().width;
    if (!width) return;
    this.slotSize = width / GRID_SIZE;
    var cellSize = this.slotSize - GAP;
    for (var row = 0; row < this.board.length; row++) {
      for (var col = 0; col < this.board[row].length; col++) {
        var tile = this.board[row][col];
        if (!tile) continue;
        this._positionTile(tile, cellSize, instant);
      }
    }
  };

  TombiniGame.prototype._positionTile = function (tile, cellSize, instant) {
    var size = cellSize || this.slotSize - GAP;
    if (instant) {
      tile.el.style.transition = 'none';
    }
    tile.el.style.width = size + 'px';
    tile.el.style.height = size + 'px';
    tile.el.style.left = tile.col * this.slotSize + GAP / 2 + 'px';
    tile.el.style.top = tile.row * this.slotSize + GAP / 2 + 'px';
    if (instant) {
      // eslint-disable-next-line no-unused-expressions
      tile.el.offsetHeight; /* force reflow */
      tile.el.style.transition = '';
    }
  };

  /* ---------------------------------------------------------------
   * Input: click/tap + drag
   * ------------------------------------------------------------- */

  TombiniGame.prototype._onPointerDown = function (tile, ev) {
    if (this.isBusy) return;
    this.dragStart = { tile: tile, x: ev.clientX, y: ev.clientY };
  };

  TombiniGame.prototype._onPointerUp = function (tile, ev) {
    if (this.isBusy || !this.dragStart) return;
    var start = this.dragStart;
    this.dragStart = null;
    var dx = ev.clientX - start.x;
    var dy = ev.clientY - start.y;
    var dist = Math.sqrt(dx * dx + dy * dy);
    var THRESHOLD = 16;

    if (dist >= THRESHOLD && start.tile === tile) {
      var target = null;
      if (Math.abs(dx) > Math.abs(dy)) {
        target = this._neighbor(start.tile, 0, dx > 0 ? 1 : -1);
      } else {
        target = this._neighbor(start.tile, dy > 0 ? 1 : -1, 0);
      }
      if (target) {
        this._deselect();
        this._trySwap(start.tile, target);
        return;
      }
    }
    this._handleSelect(tile);
  };

  TombiniGame.prototype._neighbor = function (tile, dRow, dCol) {
    var r = tile.row + dRow;
    var c = tile.col + dCol;
    if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE) return null;
    return this.board[r][c];
  };

  TombiniGame.prototype._isAdjacent = function (a, b) {
    var dr = Math.abs(a.row - b.row);
    var dc = Math.abs(a.col - b.col);
    return dr + dc === 1;
  };

  TombiniGame.prototype._handleSelect = function (tile) {
    if (this.isBusy) return;
    if (!this.selected) {
      this.selected = tile;
      tile.el.classList.add('is-selected');
      return;
    }
    if (this.selected === tile) {
      this._deselect();
      return;
    }
    if (this._isAdjacent(this.selected, tile)) {
      var a = this.selected;
      this._deselect();
      this._trySwap(a, tile);
    } else {
      this._deselect();
      this.selected = tile;
      tile.el.classList.add('is-selected');
    }
  };

  TombiniGame.prototype._deselect = function () {
    if (this.selected) {
      this.selected.el.classList.remove('is-selected');
      this.selected = null;
    }
  };

  /* ---------------------------------------------------------------
   * Scambio
   * ------------------------------------------------------------- */

  TombiniGame.prototype._swapModel = function (a, b) {
    var ar = a.row, ac = a.col;
    var br = b.row, bc = b.col;
    this.board[ar][ac] = b;
    this.board[br][bc] = a;
    a.row = br; a.col = bc;
    b.row = ar; b.col = ac;
  };

  TombiniGame.prototype._trySwap = function (a, b) {
    var self = this;
    this.isBusy = true;
    this._swapModel(a, b);
    var cellSize = this.slotSize - GAP;
    this._positionTile(a, cellSize, false);
    this._positionTile(b, cellSize, false);

    return sleep(FALL_MS).then(function () {
      var bothSpecial =
        a.special && b.special && a.special !== 'colorbomb' && b.special !== 'colorbomb';
      var anyColorbomb = a.special === 'colorbomb' || b.special === 'colorbomb';

      if (anyColorbomb) {
        return self._resolveColorbombSwap(a, b);
      }
      if (bothSpecial) {
        return self._resolveSpecialCombo(a, b);
      }
      var matches = self._findAllMatches();
      if (matches.length) {
        return self._resolveMatchCascade({ row: b.row, col: b.col });
      }
      // scambio non valido: annulla
      self._swapModel(a, b);
      self._positionTile(a, cellSize, false);
      self._positionTile(b, cellSize, false);
      a.el.classList.add('is-invalid');
      b.el.classList.add('is-invalid');
      return sleep(320).then(function () {
        a.el.classList.remove('is-invalid');
        b.el.classList.remove('is-invalid');
      });
    }).then(function () {
      self.isBusy = false;
    });
  };

  /* ---------------------------------------------------------------
   * Ricerca match
   * ------------------------------------------------------------- */

  TombiniGame.prototype._findAllMatches = function () {
    var runs = [];
    var row, col, tile;

    for (row = 0; row < GRID_SIZE; row++) {
      var runStart = 0;
      for (col = 1; col <= GRID_SIZE; col++) {
        var prev = this.board[row][col - 1];
        var cur = col < GRID_SIZE ? this.board[row][col] : null;
        var sameType =
          cur && prev &&
          prev.special !== 'colorbomb' && cur.special !== 'colorbomb' &&
          cur.type === prev.type;
        if (!sameType) {
          var len = col - runStart;
          if (len >= 3) {
            var tiles = [];
            for (var k = runStart; k < col; k++) tiles.push(this.board[row][k]);
            runs.push({ tiles: tiles, dir: 'h' });
          }
          runStart = col;
        }
      }
    }

    for (col = 0; col < GRID_SIZE; col++) {
      var vStart = 0;
      for (row = 1; row <= GRID_SIZE; row++) {
        var prevV = this.board[row - 1][col];
        var curV = row < GRID_SIZE ? this.board[row][col] : null;
        var sameTypeV =
          curV && prevV &&
          prevV.special !== 'colorbomb' && curV.special !== 'colorbomb' &&
          curV.type === prevV.type;
        if (!sameTypeV) {
          var lenV = row - vStart;
          if (lenV >= 3) {
            var tilesV = [];
            for (var kk = vStart; kk < row; kk++) tilesV.push(this.board[kk][col]);
            runs.push({ tiles: tilesV, dir: 'v' });
          }
          vStart = row;
        }
      }
    }

    return runs;
  };

  /* Raggruppa run che condividono tessere (forme a L/T) */
  TombiniGame.prototype._clusterRuns = function (runs) {
    var clusters = [];
    runs.forEach(function (run) {
      var merged = null;
      for (var i = 0; i < clusters.length; i++) {
        var cluster = clusters[i];
        var overlaps = cluster.tiles.some(function (t) {
          return run.tiles.indexOf(t) !== -1;
        });
        if (overlaps) {
          merged = cluster;
          break;
        }
      }
      if (merged) {
        merged.runs.push(run);
        run.tiles.forEach(function (t) {
          if (merged.tiles.indexOf(t) === -1) merged.tiles.push(t);
        });
      } else {
        clusters.push({ runs: [run], tiles: run.tiles.slice() });
      }
    });

    // fondi cluster che sono finiti collegati indirettamente
    var changed = true;
    while (changed) {
      changed = false;
      outer: for (var i = 0; i < clusters.length; i++) {
        for (var j = i + 1; j < clusters.length; j++) {
          var a = clusters[i], b = clusters[j];
          var overlap = a.tiles.some(function (t) {
            return b.tiles.indexOf(t) !== -1;
          });
          if (overlap) {
            a.runs = a.runs.concat(b.runs);
            b.tiles.forEach(function (t) {
              if (a.tiles.indexOf(t) === -1) a.tiles.push(t);
            });
            clusters.splice(j, 1);
            changed = true;
            break outer;
          }
        }
      }
    }
    return clusters;
  };

  /* ---------------------------------------------------------------
   * Effetti speciali: area colpita
   * ------------------------------------------------------------- */

  TombiniGame.prototype._areaForSpecial = function (tile) {
    var self = this;
    var out = [];
    if (tile.special === 'stripedH') {
      for (var c = 0; c < GRID_SIZE; c++) {
        var t = this.board[tile.row][c];
        if (t) out.push(t);
      }
    } else if (tile.special === 'stripedV') {
      for (var r = 0; r < GRID_SIZE; r++) {
        var t2 = this.board[r][tile.col];
        if (t2) out.push(t2);
      }
    } else if (tile.special === 'wrapped') {
      this._blockTiles(tile.row, tile.col, 1).forEach(function (t3) {
        out.push(t3);
      });
    }
    return out;
  };

  TombiniGame.prototype._blockTiles = function (row, col, radius) {
    var out = [];
    for (var r = row - radius; r <= row + radius; r++) {
      for (var c = col - radius; c <= col + radius; c++) {
        if (r < 0 || r >= GRID_SIZE || c < 0 || c >= GRID_SIZE) continue;
        var t = this.board[r][c];
        if (t) out.push(t);
      }
    }
    return out;
  };

  /* Espande un insieme di tessere "seed" includendo le catene di
     attivazione degli speciali coinvolti. Ritorna un Set di tessere. */
  TombiniGame.prototype._expandRemoval = function (seedTiles) {
    var removed = new Set();
    var activated = new Set();
    var queue = seedTiles.slice();
    while (queue.length) {
      var t = queue.shift();
      if (!t || removed.has(t)) continue;
      removed.add(t);
      if (t.special && t.special !== 'colorbomb' && !activated.has(t)) {
        activated.add(t);
        this._areaForSpecial(t).forEach(function (at) {
          if (at && !removed.has(at)) queue.push(at);
        });
      }
    }
    return removed;
  };

  /* ---------------------------------------------------------------
   * Risoluzione match "normali" (con eventuale creazione di speciali)
   * ------------------------------------------------------------- */

  TombiniGame.prototype._resolveMatchCascade = function (swapHint) {
    var self = this;

    function step() {
      var runs = self._findAllMatches();
      if (!runs.length) {
        return self._afterStable();
      }
      var clusters = self._clusterRuns(runs);
      var seed = [];
      var survivors = []; // {tile, special}
      var scoreGain = 0;

      clusters.forEach(function (cluster) {
        var tiles = cluster.tiles;
        var totalLen = tiles.length;
        var isCross = cluster.runs.length > 1;
        var specialType = null;

        if (isCross && totalLen >= 5) {
          specialType = 'wrapped';
        } else if (!isCross) {
          var run = cluster.runs[0];
          if (run.tiles.length === 4) {
            specialType = run.dir === 'h' ? 'stripedH' : 'stripedV';
          } else if (run.tiles.length >= 5) {
            specialType = 'colorbomb';
          }
        } else if (totalLen >= 5) {
          specialType = 'wrapped';
        }

        var survivorTile = null;
        if (specialType) {
          // Preferisci come "sopravvivente" (la tessera che diventa
          // speciale) una tessera ancora normale: se quella scelta per
          // posizione ha già uno speciale, va comunque rimossa e fatta
          // esplodere a catena, non conservata silenziosamente.
          var plainTiles = tiles.filter(function (t) {
            return !t.special;
          });
          var pool = plainTiles.length ? plainTiles : tiles;

          if (swapHint) {
            survivorTile = pool.find(function (t) {
              return t.row === swapHint.row && t.col === swapHint.col;
            });
          }
          if (!survivorTile && isCross) {
            // tessera condivisa dalle run che si intersecano
            survivorTile = pool.find(function (t) {
              return cluster.runs.every(function (r) {
                return r.tiles.indexOf(t) !== -1;
              });
            });
          }
          if (!survivorTile) {
            survivorTile = pool[Math.floor(pool.length / 2)];
          }
        }

        tiles.forEach(function (t) {
          if (t === survivorTile) return;
          seed.push(t);
        });

        scoreGain += tiles.length * SCORE_TILE;
        if (specialType === 'stripedH' || specialType === 'stripedV') scoreGain += SCORE_STRIPED;
        else if (specialType === 'wrapped') scoreGain += SCORE_WRAPPED;
        else if (specialType === 'colorbomb') scoreGain += SCORE_COLORBOMB;

        if (specialType && survivorTile) {
          survivors.push({ tile: survivorTile, special: specialType });
        }
      });

      var removalSet = self._expandRemoval(seed);
      // non rimuovere mai le tessere "sopravvissute" che diventano speciali
      survivors.forEach(function (s) {
        removalSet.delete(s.tile);
      });

      self._addScore(scoreGain + (removalSet.size - seed.length > 0 ? (removalSet.size - seed.length) * SCORE_TILE : 0));

      survivors.forEach(function (s) {
        s.tile.special = s.special;
        self._paintTile(s.tile);
      });

      return self._removeTiles(removalSet).then(function () {
        return self._collapseAndRefill();
      }).then(function () {
        return step();
      });
    }

    return step();
  };

  /* ---------------------------------------------------------------
   * Combo: colorbomb coinvolta nello scambio
   * ------------------------------------------------------------- */

  TombiniGame.prototype._allTilesOfColor = function (type) {
    var out = [];
    for (var r = 0; r < GRID_SIZE; r++) {
      for (var c = 0; c < GRID_SIZE; c++) {
        var t = this.board[r][c];
        if (t && t.type === type && t.special !== 'colorbomb') out.push(t);
      }
    }
    return out;
  };

  TombiniGame.prototype._resolveColorbombSwap = function (a, b) {
    var self = this;
    var bombTile = a.special === 'colorbomb' ? a : b;
    var otherTile = a.special === 'colorbomb' ? b : a;
    var scoreGain = SCORE_COLORBOMB;
    var seed = [];

    if (otherTile.special === 'colorbomb') {
      // due bombe di colore: svuota tutta la griglia
      for (var r = 0; r < GRID_SIZE; r++) {
        for (var c = 0; c < GRID_SIZE; c++) {
          if (this.board[r][c]) seed.push(this.board[r][c]);
        }
      }
      scoreGain += seed.length * SCORE_TILE;
    } else if (!otherTile.special) {
      // bomba + tombino normale: elimina tutti i tombini di quel colore
      var sameColor = this._allTilesOfColor(otherTile.type);
      seed = sameColor.concat([bombTile]);
      scoreGain += sameColor.length * SCORE_TILE;
    } else {
      // bomba + speciale: trasforma tutti i tombini di quel colore nello stesso speciale
      var sameColor2 = this._allTilesOfColor(otherTile.type).filter(function (t) {
        return t !== otherTile;
      });
      sameColor2.forEach(function (t) {
        t.special = otherTile.special;
        self._paintTile(t);
      });
      seed = sameColor2.concat([bombTile, otherTile]);
      scoreGain += sameColor2.length * (SCORE_TILE + SCORE_STRIPED);
    }

    var removalSet = this._expandRemoval(seed);
    this._addScore(scoreGain);

    return this._removeTiles(removalSet).then(function () {
      return self._collapseAndRefill();
    }).then(function () {
      return self._resolveMatchCascade(null);
    });
  };

  /* ---------------------------------------------------------------
   * Combo: due speciali (non colorbomb) scambiati insieme
   * ------------------------------------------------------------- */

  TombiniGame.prototype._resolveSpecialCombo = function (a, b) {
    var self = this;
    var seed = [];
    var scoreGain = SCORE_WRAPPED;
    var row = b.row, col = b.col; // punto dello scambio

    var isStriped = function (t) {
      return t.special === 'stripedH' || t.special === 'stripedV';
    };

    if (isStriped(a) && isStriped(b)) {
      for (var c = 0; c < GRID_SIZE; c++) {
        var th = this.board[row][c];
        if (th) seed.push(th);
      }
      for (var r = 0; r < GRID_SIZE; r++) {
        var tv = this.board[r][col];
        if (tv) seed.push(tv);
      }
    } else if (a.special === 'wrapped' && b.special === 'wrapped') {
      seed = this._blockTiles(row, col, 2);
      scoreGain = SCORE_WRAPPED * 2;
    } else {
      // a righe + incartato: tre righe o tre colonne (a seconda dell'orientamento)
      var striped = isStriped(a) ? a : b;
      if (striped.special === 'stripedH') {
        for (var rr = row - 1; rr <= row + 1; rr++) {
          if (rr < 0 || rr >= GRID_SIZE) continue;
          for (var cc = 0; cc < GRID_SIZE; cc++) {
            var t2 = this.board[rr][cc];
            if (t2) seed.push(t2);
          }
        }
      } else {
        for (var cc2 = col - 1; cc2 <= col + 1; cc2++) {
          if (cc2 < 0 || cc2 >= GRID_SIZE) continue;
          for (var rr2 = 0; rr2 < GRID_SIZE; rr2++) {
            var t3 = this.board[rr2][cc2];
            if (t3) seed.push(t3);
          }
        }
      }
      scoreGain = SCORE_WRAPPED * 1.5;
    }

    var removalSet = this._expandRemoval(seed);
    this._addScore(scoreGain + removalSet.size * SCORE_TILE);

    return this._removeTiles(removalSet).then(function () {
      return self._collapseAndRefill();
    }).then(function () {
      return self._resolveMatchCascade(null);
    });
  };

  /* ---------------------------------------------------------------
   * Rimozione, gravità, refill
   * ------------------------------------------------------------- */

  TombiniGame.prototype._removeTiles = function (tileSet) {
    var self = this;
    if (!tileSet.size) return Promise.resolve();

    var blastRow = 0, blastCol = 0, hasBlast = false;
    tileSet.forEach(function (t) {
      t.el.classList.add('is-clearing');
      blastRow += t.row;
      blastCol += t.col;
      hasBlast = true;
    });
    if (hasBlast && tileSet.size >= 5) {
      this._spawnBlast(blastRow / tileSet.size, blastCol / tileSet.size);
    }

    return sleep(CLEAR_MS).then(function () {
      tileSet.forEach(function (t) {
        if (t.el.parentNode) t.el.parentNode.removeChild(t.el);
        self.board[t.row][t.col] = null;
      });
    });
  };

  TombiniGame.prototype._spawnBlast = function (row, col) {
    var el = document.createElement('div');
    el.className = 'tombini-game__blast';
    var size = this.slotSize * 3;
    el.style.width = size + 'px';
    el.style.height = size + 'px';
    el.style.left = (col + 0.5) * this.slotSize - size / 2 + 'px';
    el.style.top = (row + 0.5) * this.slotSize - size / 2 + 'px';
    this.boardEl.appendChild(el);
    setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, 450);
  };

  TombiniGame.prototype._collapseAndRefill = function () {
    var cellSize = this.slotSize - GAP;
    var col, row;
    for (col = 0; col < GRID_SIZE; col++) {
      var writeRow = GRID_SIZE - 1;
      for (row = GRID_SIZE - 1; row >= 0; row--) {
        var tile = this.board[row][col];
        if (tile) {
          if (writeRow !== row) {
            this.board[writeRow][col] = tile;
            this.board[row][col] = null;
            tile.row = writeRow;
            this._positionTile(tile, cellSize, false);
          }
          writeRow--;
        }
      }
      var missing = writeRow + 1;
      for (var k = 0; k < missing; k++) {
        var finalRow = writeRow - k;
        var type = randInt(TYPE_COUNT) + 1;
        var newTile = this._createTile(finalRow, col, type, null);
        this.board[finalRow][col] = newTile;
        this.boardEl.appendChild(newTile.el);
        newTile.el.style.transition = 'none';
        newTile.el.style.width = cellSize + 'px';
        newTile.el.style.height = cellSize + 'px';
        newTile.el.style.left = col * this.slotSize + GAP / 2 + 'px';
        newTile.el.style.top = (-1 - k) * this.slotSize + GAP / 2 + 'px';
        // eslint-disable-next-line no-unused-expressions
        newTile.el.offsetHeight; /* forza il reflow prima di riattivare la transizione */
        newTile.el.style.transition = '';
        newTile.el.classList.add('is-entering');
        // ora che la transizione è riattivata, sposta la tessera nella sua
        // posizione finale: il browser anima automaticamente la caduta.
        this._positionTile(newTile, cellSize, false);
      }
    }

    return sleep(FALL_MS + 40).then(function () {});
  };

  /* ---------------------------------------------------------------
   * Stabilità: nessuna mossa disponibile -> rimescola
   * ------------------------------------------------------------- */

  TombiniGame.prototype._hasAvailableMoves = function () {
    for (var row = 0; row < GRID_SIZE; row++) {
      for (var col = 0; col < GRID_SIZE; col++) {
        var tile = this.board[row][col];
        if (!tile) continue;
        if (tile.special === 'colorbomb') return true;
        var right = col + 1 < GRID_SIZE ? this.board[row][col + 1] : null;
        var down = row + 1 < GRID_SIZE ? this.board[row + 1][col] : null;
        if (right && this._wouldMatch(row, col, row, col + 1)) return true;
        if (down && this._wouldMatch(row, col, row + 1, col)) return true;
      }
    }
    return false;
  };

  TombiniGame.prototype._wouldMatch = function (r1, c1, r2, c2) {
    var t1 = this.board[r1][c1];
    var t2 = this.board[r2][c2];
    if (!t1 || !t2) return false;
    if (t1.special === 'colorbomb' || t2.special === 'colorbomb') return true;
    if (t1.special && t2.special) return true; // combo sempre valida

    var typeAt = function (self, r, c) {
      if (r === r1 && c === c1) return t2.type;
      if (r === r2 && c === c2) return t1.type;
      var t = self.board[r] && self.board[r][c];
      if (!t || t.special === 'colorbomb') return null;
      return t.type;
    };
    var self = this;

    function checkLine(r, c, dr, dc) {
      var t = typeAt(self, r, c);
      if (t == null) return false;
      var count = 1;
      var rr = r + dr, cc = c + dc;
      while (rr >= 0 && rr < GRID_SIZE && cc >= 0 && cc < GRID_SIZE && typeAt(self, rr, cc) === t) {
        count++; rr += dr; cc += dc;
      }
      rr = r - dr; cc = c - dc;
      while (rr >= 0 && rr < GRID_SIZE && cc >= 0 && cc < GRID_SIZE && typeAt(self, rr, cc) === t) {
        count++; rr -= dr; cc -= dc;
      }
      return count >= 3;
    }

    return (
      checkLine(r1, c1, 0, 1) || checkLine(r1, c1, 1, 0) ||
      checkLine(r2, c2, 0, 1) || checkLine(r2, c2, 1, 0)
    );
  };

  TombiniGame.prototype._ensureSolvable = function () {
    var tries = 0;
    while (!this._hasAvailableMoves() && tries < 40) {
      this._reshuffleTypesOnly();
      tries++;
    }
  };

  /* Riassegna i tipi cella per cella con la stessa regola "niente match
     già risolti" usata per la griglia iniziale, così un rimescolamento
     non lascia mai allineamenti già formati sul tabellone. */
  TombiniGame.prototype._reshuffleTypesOnly = function () {
    for (var row = 0; row < GRID_SIZE; row++) {
      for (var col = 0; col < GRID_SIZE; col++) {
        var tile = this.board[row][col];
        if (!tile) continue;
        tile.special = null;
        tile.type = this._randomTypeNoMatch(row, col);
        this._paintTile(tile);
      }
    }
  };

  TombiniGame.prototype._afterStable = function () {
    var self = this;
    if (this._hasAvailableMoves()) return Promise.resolve();
    this._setMessage('Rimescolo la griglia…');
    return sleep(500).then(function () {
      self._ensureSolvable();
      self._setMessage('');
    });
  };

  /* ---------------------------------------------------------------
   * Punteggio
   * ------------------------------------------------------------- */

  TombiniGame.prototype._addScore = function (amount) {
    if (!amount) return;
    this.score += Math.round(amount);
    if (this.scoreEl) {
      this.scoreEl.textContent = this.score;
      this.scoreEl.classList.add('tombini-game__score-value--bump');
      var self = this;
      setTimeout(function () {
        self.scoreEl.classList.remove('tombini-game__score-value--bump');
      }, 160);
    }
  };

  /* ---------------------------------------------------------------
   * Rigioca
   * ------------------------------------------------------------- */

  TombiniGame.prototype.restart = function () {
    if (this.isBusy) return;
    this._deselect();
    this.score = 0;
    if (this.scoreEl) this.scoreEl.textContent = '0';
    this._setMessage('');
    this._buildInitialBoard();
  };

  /* ---------------------------------------------------------------
   * Avvio
   * ------------------------------------------------------------- */

  function initAll() {
    var roots = document.querySelectorAll('.tombini-game');
    roots.forEach(function (root) {
      if (root.__tombiniGame) return;
      root.__tombiniGame = new TombiniGame(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
